import { useCallback, useEffect, useState } from 'react';
import type { Lang } from '../pages/landingCopy.js';

export type { Lang };

const KEY = 'ro_lang';
/** Fired on the window so every mounted component re-reads at once. */
const EVENT = 'ro:lang';

/**
 * The reader's language, for pages that have no `/tr` in their URL.
 *
 * The marketing pages carry their language in the path, because they are
 * shared and indexed. The sign-in screen is not: someone arrives at `/login`
 * from an email link or a bookmark, with nothing in the URL to go on. So the
 * choice is remembered — set when they pick a language on the landing page,
 * and otherwise taken from the browser.
 */
export function resolveLang(): Lang {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === 'tr' || stored === 'en') return stored;
  } catch {
    // Private windows and blocked site data throw here. Fall through to the
    // browser's own preference rather than failing to render a sign-in page.
  }
  if (typeof navigator !== 'undefined') {
    const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
    if (langs.some((l) => l?.toLowerCase().startsWith('tr'))) return 'tr';
  }
  return 'en';
}

export function setLang(lang: Lang): void {
  try {
    localStorage.setItem(KEY, lang);
  } catch {
    // Not being able to remember it is survivable; not switching is not.
  }
  window.dispatchEvent(new CustomEvent(EVENT));
}

/** The current language, plus a setter that updates every subscriber. */
export function useLang(): [Lang, (l: Lang) => void] {
  const [lang, setState] = useState<Lang>(resolveLang);

  useEffect(() => {
    const sync = () => setState(resolveLang());
    window.addEventListener(EVENT, sync);
    // Another tab switching language should not leave this one behind.
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const change = useCallback((l: Lang) => setLang(l), []);
  return [lang, change];
}

/** Picks the value for the active language from a two-language record. */
export function t<T>(lang: Lang, dict: Record<Lang, T>): T {
  return dict[lang];
}
